/* eslint-env browser */

import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";
import { normalizeStatsServiceNoteResponse } from "./statsServiceNote";
import type { StatsRangeKey, StatsServiceNoteResponse } from "./statsDashboardTypes";

interface UseStatsServiceNoteParams {
  brand: string;
  rangeKey: StatsRangeKey;
  selectedCenters: string[];
  selectedClientTypes: string[];
}

interface UseStatsServiceNoteResult {
  loading: boolean;
  data: StatsServiceNoteResponse;
  error: string | null;
}

const EMPTY_NOTE_RESPONSE: StatsServiceNoteResponse = {
  available: false,
  officialView: false,
  reason: "not_generated_yet",
  generatedAt: null,
  note: null,
  markdown: null,
};

function normalizeStringArray(values: string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, "fr"),
  );
}

export function useStatsServiceNote({
  brand,
  rangeKey,
  selectedCenters,
  selectedClientTypes,
}: UseStatsServiceNoteParams): UseStatsServiceNoteResult {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<StatsServiceNoteResponse>(EMPTY_NOTE_RESPONSE);
  const [error, setError] = useState<string | null>(null);

  const centers = useMemo(() => normalizeStringArray(selectedCenters), [selectedCenters]);
  const clientTypes = useMemo(
    () => normalizeStringArray(selectedClientTypes),
    [selectedClientTypes],
  );
  const centersKey = centers.join("\u0001");
  const clientTypesKey = clientTypes.join("\u0001");

  useEffect(() => {
    let cancelled = false;

    const fetchNote = async () => {
      setLoading(true);
      setError(null);

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        const token = session?.access_token;
        if (!token) {
          throw new Error("Utilisateur non authentifié");
        }

        const params = new globalThis.URLSearchParams({
          brand,
          rangeKey,
        });

        for (const center of centers) {
          params.append("center", center);
        }

        for (const clientType of clientTypes) {
          params.append("clientType", clientType);
        }

        const response = await globalThis.fetch(`/api/stats/service-note?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = await response.json();

        if (!response.ok) {
          throw new Error(
            typeof payload?.message === "string" ? payload.message : "Lecture de la note impossible",
          );
        }

        if (cancelled) return;

        setData(normalizeStatsServiceNoteResponse(payload));
      } catch (err) {
        if (cancelled) return;
        setData(EMPTY_NOTE_RESPONSE);
        setError(err instanceof Error ? err.message : "Lecture de la note impossible");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchNote();

    return () => {
      cancelled = true;
    };
  }, [brand, rangeKey, centersKey, clientTypesKey]);

  return {
    loading,
    data,
    error,
  };
}
